import { useContext } from 'react';
import type ModelType from '@/lib/constants/challengeModel/ModelType.enum';
import Section from './Section';
import SectionName from './Section/SectionName';
import SectionContentWrapper from './Section/SectionContentWrapper';
import SectionLeftContent from './Section/SectionLeftContent';
import SectionRightContent from './Section/SectionRightContent';
import SectionBottomContent from './Section/SectopmBottomContent';
import ContentGroupType from '@/lib/constants/challengeModel/ContentGroupType.enum';
import sortByPosition from '@/lib/utils/sorting/sortByPosition';
import FieldRenderer from './FieldRenderer';
import PortfolioItemContext from '../PortfolioItemContext';
import useItemIsVisibleToPortfolioUser from './useItemIsVisibleToPortfolioUser';
import createdAtMatchesModelVisibilityDates from './createdAtMatchesModelVisibilityDates';
import useSectionNameColor from './useSectionNameColor';
import usePortfolioItemModel from './usePortfolioItemModel';

type PortfolioItemFieldsProps = {
  modelType: ModelType;
};

const PortfolioItemFields = ({ modelType }: PortfolioItemFieldsProps) => {
  const model = usePortfolioItemModel(modelType);
  const sectionNameColor = useSectionNameColor();

  const { portfolioItem } = useContext(PortfolioItemContext);
  const itemIsVisibleToPortfolioUser = useItemIsVisibleToPortfolioUser();

  if (model === null) {
    return null;
  }

  return (
    <>
      {model.sections
        .filter(section =>
          createdAtMatchesModelVisibilityDates(
            portfolioItem.createdAt,
            section,
          ),
        )
        .filter(itemIsVisibleToPortfolioUser)
        .sort(sortByPosition)
        .map(section => (
          <Section key={section.id}>
            <SectionName customColor={sectionNameColor}>
              {section.name}
            </SectionName>
            <SectionContentWrapper>
              <SectionLeftContent>
                <FieldRenderer
                  fields={section.fields.filter(
                    field => field.contentGroup === ContentGroupType.LEFT,
                  )}
                />
              </SectionLeftContent>
              <SectionRightContent>
                <FieldRenderer
                  fields={section.fields.filter(
                    field => field.contentGroup === ContentGroupType.RIGHT,
                  )}
                  disableCoachComments
                />
              </SectionRightContent>
            </SectionContentWrapper>
            {section.fields.filter(
              field => field.contentGroup === ContentGroupType.BOTTOM,
            ).length > 0 && (
              <SectionBottomContent>
                <FieldRenderer
                  fields={section.fields.filter(
                    field => field.contentGroup === ContentGroupType.BOTTOM,
                  )}
                />
              </SectionBottomContent>
            )}
          </Section>
        ))}
    </>
  );
};

export default PortfolioItemFields;
